import React, {
	ChangeEvent,
	FC,
	ReactElement,
	useCallback,
	useContext,
	useEffect,
	useState,
} from "react";
import clsx from "clsx";
import fp from "lodash/fp";
import { Form, Col } from "react-bootstrap";
import { useFormContext, Validate } from "react-hook-form";

import {
	WarningMessage,
	ErrorMessage,
	Tooltip,
	RequiredMark,
	Skeleton,
} from "components";
import { BrandingColorContext } from "components/contexts";

import styles from "./fields.module.scss";

const defaultProps = {
	emptyLabel: false,
	type: "text",
	colLabel: 6,
	colInput: 6,
};

export type InputProps = {
	name: string;
	register: Function;
	defaultValue?: any;
	label?: string;
	labelHtml?: ReactElement<any, any>;
	errors?: any;
	emptyLabel?: typeof defaultProps.emptyLabel;
	isInvalid?: boolean;
	type?: typeof defaultProps.type;
	placeholder?: string;
	colLabel?: typeof defaultProps.colLabel;
	colInput?: typeof defaultProps.colInput;
	rows?: number;
	normalizeOnBlur?: Function;
	normalize?: Array<Function> | Function;
	validator?: Validate<any, any> | Record<string, Validate<any, any>>;
	disabled?: boolean;
	isSelectionDisabled?: boolean;
	readOnly?: boolean;
	loader?: FC | ((args: any) => JSX.Element);
	loading?: boolean; // Some operation being proceed. E.g. custom async validation
	blurLoading?: boolean; // Display blur instead of the input field
	onChange?: Function;
	onBlur?: Function;
	onFocus?: Function;
	className?: string;
	warningClassName?: string | Object;
	errorClassName?: string;
	as?: any;
	showRequiredMark?: boolean;
	labelClassName?: string;
	valueClassName?: string;
	inputBlockClassName?: string;
	warningText?: string;
	showWarningOnFocus?: boolean;
	initialValueField?: any;
	isTooltip?: boolean;
	tooltipTitle?: string;
	autoComplete?: string;
	isPlainText?: boolean;
	placeholderPosition?: "right" | "left" | "center";
	canShowErrorIcon?: boolean;
	solveCaretIssue?: Function;
	addon?: string | React.ReactNode;
	external?: React.ReactNode;
	maxLength?: number;
};

const InputFieldWithLoader = ({
	inputField,
	colInput,
	inputBlockClassName,
	blurLoading,
	loading,
	loader,
}) => {
	return (
		<>
			<Col sm={colInput} className={clsx(inputBlockClassName)}>
				{blurLoading && <Skeleton height="34px" />}
				{inputField}
			</Col>
			{!blurLoading && loading && loader ? loader({}) : null}
		</>
	);
};

const HookFormInput = ({
	name,
	register,
	defaultValue,
	label,
	labelHtml,
	errors,
	emptyLabel = defaultProps.emptyLabel,
	isInvalid,
	type = defaultProps.type,
	placeholder,
	colLabel = defaultProps.colLabel,
	colInput = defaultProps.colInput,
	rows,
	normalizeOnBlur,
	normalize,
	validator,
	disabled,
	isSelectionDisabled,
	loader,
	loading,
	blurLoading,
	onChange,
	onBlur,
	onFocus,
	showRequiredMark,
	labelClassName,
	valueClassName,
	errorClassName,
	inputBlockClassName,
	as,
	warningText,
	warningClassName,
	showWarningOnFocus,
	readOnly,
	initialValueField,
	isTooltip,
	tooltipTitle,
	autoComplete,
	isPlainText = false,
	placeholderPosition = "left",
	canShowErrorIcon,
	solveCaretIssue,
	addon,
	external,
	maxLength,
}: InputProps) => {
	const formMethods = useFormContext();

	const [prevValue, setPrevValue] = useState<any>(undefined);
	const [currentValue, setCurrentValue] = useState("");
	const [inputFocused, setInputFocused] = useState(false);
	const [isEllipsis, setIsEllipsis] = useState(false);
	const [isFocus, setIsFocus] = useState(false);

	const error =
		!disabled && fp.get(name, formMethods?.formState?.errors || errors);

	const invalidClass =
		error && "message" in error && clsx(styles.inputError, "is-invalid");

	const disabledClass = disabled && styles.disabled;

	const currentTooltipTitle =
		tooltipTitle || (isTooltip && currentValue) || " ";

	const valuesFromHookForm = formMethods?.getValues?.(name);
	useEffect(() => {
		if (initialValueField !== undefined) {
			setPrevValue(initialValueField);
		}
	}, [initialValueField]);

	const normalizationActive = () => {
		const value = formMethods?.getValues?.(name);
		if (value && normalizeOnBlur) {
			const result = normalizeOnBlur(value);

			if (result !== value && prevValue === undefined && !isFocus) {
				setCurrentValue(result);
				formMethods?.setValue?.(name, result);
			}
		}
	};

	useEffect(() => {
		// nomalize afterSave
		normalizationActive();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [valuesFromHookForm]);

	const labelBlock =
		label || emptyLabel ? (
			<Form.Label
				className={clsx(
					styles.wrappLabel,
					labelClassName,
					disabledClass,
					isPlainText && styles.plaintext,
				)}
				column
				onClick={e => e.preventDefault()}
				sm={colLabel}
				id={name}
			>
				{labelHtml || label}
				{showRequiredMark && <RequiredMark />}
			</Form.Label>
		) : null;

	const onBlurHandler = event => {
		if (normalizeOnBlur) {
			event.target.value = normalizeOnBlur(event.target.value);
			formMethods?.setValue(name, event.target.value);
			setPrevValue(normalizeOnBlur(event.target.value));
		}
		if (onBlur) onBlur(event);
		setInputFocused(false);
		onFocus?.(false);
	};

	const onFocusHandler = event => {
		showWarningOnFocus && setInputFocused(true);
		onFocus?.(true);
		setIsFocus(true);
	};

	const onMouseEnterHandler = useCallback(
		e => {
			if (!isTooltip && !tooltipTitle) {
				return;
			}

			const { scrollWidth, clientWidth } = e.currentTarget;
			setIsEllipsis(scrollWidth > clientWidth);
		},
		[isTooltip, tooltipTitle],
	);

	const onMouseLeaveHandler = useCallback(() => {
		if (!isTooltip && !tooltipTitle) {
			return;
		}
		setIsEllipsis(false);
	}, [isTooltip, tooltipTitle]);

	const onMouseDownHandler = useCallback(
		e => {
			if (isSelectionDisabled) {
				e.preventDefault();
			}
		},
		[isSelectionDisabled],
	);

	useEffect(() => {
		const value = formMethods?.getValues?.(name);
		if (value) {
			setCurrentValue(value);
		}
	}, [formMethods, name]);

	const runAfterUpdate = useRunAfterUpdate();

	function useRunAfterUpdate() {
		const afterPaintRef: any = React.useRef(null);
		React.useLayoutEffect(() => {
			if (afterPaintRef.current) {
				afterPaintRef.current();
				afterPaintRef.current = null;
			}
		});
		return fn => (afterPaintRef.current = fn);
	}

	const solveCaretIssueFunction = (event, normalize) => {
		var text: any = event.target;
		var result;
		var start;
		result = normalize(event.target.value, prevValue || "");
		let lengthPrevVal = 0;
		if (prevValue) {
			lengthPrevVal = prevValue?.length;
		}
		const diff = result.length - lengthPrevVal;
		if (text) {
			if (diff > 0) {
				start = text?.selectionStart + diff - 1;
			} else if (diff === 0) {
				start = text?.selectionStart + 1;
			} else if (text.selectionStart === 0 && diff === -2) {
				start = text.selectionStart;
			} else {
				start = text?.selectionStart + 1 + diff;
			}
		}
		runAfterUpdate(() => {
			text.selectionStart = start;
			text.selectionEnd = start;
		});
	};

	const onChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
		if (normalize && solveCaretIssue) {
			solveCaretIssueFunction(event, normalize);
		}

		if (normalize) {
			let normalized;
			if (Array.isArray(normalize)) {
				normalized = normalize.reduce(
					(val, normalizer) => normalizer(val, prevValue || ""),
					event.target.value,
				);
			} else {
				normalized = normalize(event.target.value, prevValue || "");
			}

			event.target.value = normalized;
			setCurrentValue(normalized);
			formMethods?.setValue(name, normalized);
		} else {
			setCurrentValue(event.target.value);
			formMethods?.setValue(name, event.target.value);
		}

		if (onChange) {
			onChange(event);
		}
		setPrevValue(event.target.value);
	};

	const brandingColor = useContext(BrandingColorContext);
	const isBrandingColor = brandingColor && styles.brandingColor;
	const isColorInput = type === "color" && styles.colorInput;

	// Note I tried using Controller
	// and nest <Form.Control as={as} ... /> inside
	// but it didn't work well with normalization (and validation for rare cases)
	const {
		onChange: formOnChange,
		onBlur: formOnBlur,
		name: nameForm,
		ref,
	} = register(name, {
		validate: validator || {},
	});

	const placeholderPositionStyle =
		placeholderPosition === "right"
			? styles.placeholderRightPosition
			: placeholderPosition === "center" &&
			  styles.placeholderCenterPosition;

	return (
		<>
			{labelBlock}
			<InputFieldWithLoader
				inputField={
					<div
						className={clsx(
							blurLoading && styles.hide,
							styles.inputWrapper,
							!!addon && styles.addonWrapper,
						)}
					>
						<Tooltip title={currentTooltipTitle} open={isEllipsis}>
							<Form.Control
								name={nameForm}
								aria-labelledby={name}
								ref={ref}
								defaultValue={defaultValue}
								disabled={disabled}
								placeholder={placeholder}
								rows={rows}
								onBlur={(e: any) => {
									onBlurHandler?.(e);
									formOnBlur(e);
								}}
								onChange={(e: any) => {
									onChangeHandler?.(e);
									formOnChange(e);
								}}
								onFocus={onFocusHandler}
								onMouseEnter={onMouseEnterHandler}
								onMouseLeave={onMouseLeaveHandler}
								onMouseDown={onMouseDownHandler}
								isInvalid={isInvalid}
								type={type}
								className={clsx(
									styles.field,
									invalidClass,
									isBrandingColor,
									(isTooltip || tooltipTitle) &&
										styles.ellipsis,
									isPlainText && styles.plaintext,
									valueClassName,
									isColorInput,
									placeholderPositionStyle,
								)}
								as={as}
								readOnly={readOnly}
								data-autotest={`${currentValue}`}
								autoComplete={autoComplete}
								plaintext={isPlainText}
								maxLength={maxLength}
							/>
						</Tooltip>
						{!!addon && <div className={styles.addon}>{addon}</div>}
						<ErrorMessage
							field={name}
							canShowErrorIcon={canShowErrorIcon}
							errors={formMethods?.formState?.errors}
							customStyles={errorClassName}
						/>
						<WarningMessage
							field={name}
							errors={formMethods?.formState?.errors}
							warningText={warningText}
							customStyles={warningClassName}
							hideWarning={showWarningOnFocus && !inputFocused}
						/>
						{external && (
							<div className={styles.externalHolder}>
								{external}
							</div>
						)}
					</div>
				}
				blurLoading={blurLoading}
				colInput={colInput}
				inputBlockClassName={inputBlockClassName}
				loading={loading}
				loader={loader}
			/>
		</>
	);
};

const component = HookFormInput;
export { component as HookFormInput };
